// Full-screen viewer for an item's screenshots and files: ← → between them, mark up (a new version;
// the original stays), caption, cover, "show in release notes", copy, download, delete. Firm screenshots
// from linked requests are read-only; marking one up saves a copy on the item.
import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Copy, Download, FileText, PenLine, Trash2, X } from 'lucide-react';
import { api, ApiError, authenticatedBlob } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { Button, cx } from '../../ui';
import { MarkupEditor } from './Markup';
import { itemFile, requestFile, useAdminImage, type Attachment, type FirmAttachment, type ItemDetail } from './types';

export type ViewerFile = { own: Attachment; firm?: never } | { firm: FirmAttachment; own?: never };

const msg = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);
const darkBtn = '!border-[#3A4556] !bg-[#1B222C] !text-[#E6EAF0] hover:!bg-[#263040]';

export function ScreenshotViewer({ item, files, index, onIndex, onClose, onItem }: {
  item: ItemDetail; files: ViewerFile[]; index: number; onIndex: (i: number) => void; onClose: () => void; onItem: (i: ItemDetail) => void;
}) {
  const f = files[index];
  const [marking, setMarking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [caption, setCaption] = useState('');
  const own = f?.own;
  const firm = f?.firm;
  const previous = own?.previous_id ? item.attachments.find((a) => a.id === own.previous_id) ?? null : null;
  const shown = showOriginal && previous ? previous : own;
  const isImage = (own ?? firm)?.mime_type.startsWith('image/') ?? false;
  const ready = (own ?? firm)?.scan === 'clean';
  const path = !f || !ready ? null : firm ? requestFile(firm.request_id, firm.id) : itemFile(item.id, shown!.id);
  const url = useAdminImage(isImage ? path : null);

  useEffect(() => { setMarking(false); setShowOriginal(false); setError(null); setCaption(own?.caption ?? ''); }, [index, own?.id, own?.caption]);

  const go = useCallback((d: number) => { if (files.length > 1) onIndex((index + d + files.length) % files.length); }, [files.length, index, onIndex]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (marking || (e.target as HTMLElement).closest('input, textarea')) return;
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [marking, go, onClose]);
  useEffect(() => { const prev = document.body.style.overflow; document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = prev; }; }, []);

  if (!f) return null;
  const name = (own ?? firm)!.filename;

  const patch = async (body: Record<string, unknown>) => {
    if (!own) return;
    try { onItem((await api.patch<{ item: ItemDetail }>(`/api/admin/backlog/${item.id}/attachments/${own.id}`, body)).item); }
    catch (e) { setError(msg(e, 'That change could not be saved.')); }
  };
  const saveMarkup = async (png: Blob) => {
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      const base = name.replace(/\.[^.]+$/, '');
      const file = new File([png], `${base.replace(/-marked$/, '')}-marked.png`, { type: 'image/png' });
      if (own) {
        form.append('screenshots', file, file.name);
        const res = await api.post<{ item: ItemDetail; attachment_id: string }>(`/api/admin/backlog/${item.id}/attachments/${own.id}/markup`, form);
        onItem(res.item);
      } else {
        form.append('screenshots', file, file.name);
        form.append('files_meta', JSON.stringify([{ caption: `From ${firm!.request_reference}`, annotated: true }]));
        const res = await api.post<{ item: ItemDetail; attachment_ids: string[] }>(`/api/admin/backlog/${item.id}/attachments`, form);
        onItem(res.item);
      }
      setMarking(false);
    } catch (e) { setError(msg(e, 'The marked-up copy could not be saved.')); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!own || !window.confirm(`Delete ${name}${own.previous_id ? ' and its earlier versions' : ''}?`)) return;
    try {
      const res = await api.del<{ item: ItemDetail }>(`/api/admin/backlog/${item.id}/attachments/${own.id}`);
      onItem(res.item);
      if (files.length <= 1) onClose(); else onIndex(Math.min(index, files.length - 2));
    } catch (e) { setError(msg(e, 'Could not delete the file.')); }
  };
  const copy = async () => {
    try {
      if (!url) return;
      const blob = await (await fetch(url)).blob();
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
    } catch { setError('Your browser did not allow copying the image. Use Download instead.'); }
  };
  const download = () => {
    if (!path) return;
    authenticatedBlob(path).then((b) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = (shown ?? firm)!.filename;
      a.click();
      window.setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    }, (e) => setError(msg(e, 'Could not download the file.')));
  };

  return (
    <div role="dialog" aria-modal="true" aria-label={`${name}, ${index + 1} of ${files.length}`} className="fixed inset-0 z-50 flex flex-col bg-[#11161D] text-white">
      <header className="flex flex-wrap items-center gap-2.5 border-b border-[#263040] px-5 py-3">
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="truncate font-bold">{name} <span className="font-medium text-[#AEB8CC]">· {index + 1} of {files.length}</span></span>
          <span className="truncate text-xs text-[#AEB8CC]">
            {item.reference} · {firm ? `from ${firm.uploaded_by_name}${firm.firm_name ? ` (${firm.firm_name})` : ''} via ${firm.request_reference}` : `${own!.annotated ? 'marked up by' : 'added by'} ${own!.uploaded_by_name}`} · {fmtDateTime((own ?? firm)!.created_at)}
          </span>
        </div>
        <span className="ml-auto flex flex-wrap gap-2">
          {isImage && ready && !marking && <Button size="sm" className={darkBtn} onClick={() => setMarking(true)}><PenLine aria-hidden size={15} /> {firm ? 'Mark up a copy' : 'Mark up'}</Button>}
          {isImage && ready && <Button size="sm" className={darkBtn} onClick={() => void copy()}><Copy aria-hidden size={15} /> Copy image</Button>}
          {ready && <Button size="sm" className={darkBtn} onClick={download}><Download aria-hidden size={15} /> Download</Button>}
          {own && <Button size="sm" className={cx(darkBtn, '!text-[#F3A6A6]')} onClick={() => void remove()}><Trash2 aria-hidden size={15} /> Delete</Button>}
          <Button size="sm" className={darkBtn} onClick={onClose} ><X aria-hidden size={15} /> Close</Button>
        </span>
      </header>
      {error && <div role="alert" className="bg-[#4A1D1D] px-5 py-2 text-sm text-[#F3C4C4]">{error}</div>}
      <div className="flex min-h-0 flex-1">
        <div className="relative flex min-w-0 flex-1 flex-col p-5">
          {marking && url ? (
            <MarkupEditor src={url} saving={saving} onCancel={() => setMarking(false)} onSave={(b) => void saveMarkup(b)} />
          ) : (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              {files.length > 1 && <button type="button" aria-label="Previous" onClick={() => go(-1)} className="absolute left-4 flex h-10 w-10 items-center justify-center rounded-full border border-[#3A4556] bg-[#1B222C] text-white"><ChevronLeft aria-hidden size={18} /></button>}
              {!ready ? <p className="text-[#AEB8CC]">{(own ?? firm)!.scan === 'pending' ? 'Checking this file for malware…' : 'This file failed the malware check and can’t be opened.'}</p>
                : isImage ? (url ? <img src={url} alt={own?.caption || name} className="max-h-[78vh] max-w-full rounded-md bg-white object-contain shadow-[0_20px_50px_rgba(0,0,0,0.5)]" /> : <p className="text-[#AEB8CC]">Loading…</p>)
                : <div className="flex flex-col items-center gap-3 text-[#C9D1E0]"><FileText aria-hidden size={48} /><span>{name}</span><Button size="sm" variant="primary" onClick={download}>Download</Button></div>}
              {files.length > 1 && <button type="button" aria-label="Next" onClick={() => go(1)} className="absolute right-4 flex h-10 w-10 items-center justify-center rounded-full border border-[#3A4556] bg-[#1B222C] text-white"><ChevronRight aria-hidden size={18} /></button>}
            </div>
          )}
        </div>
        <aside className="hidden w-[280px] flex-none flex-col gap-3 border-l border-[#263040] p-4 text-[13px] md:flex">
          {own ? (
            <>
              <label className="flex flex-col gap-1 text-xs font-semibold text-[#AEB8CC]">Caption
                <input value={caption} onChange={(e) => setCaption(e.target.value)} onBlur={() => { if ((caption.trim() || null) !== own.caption) void patch({ caption: caption.trim() }); }}
                  maxLength={200} className="h-9 rounded-[10px] border border-[#3A4556] bg-[#1B222C] px-2.5 text-[13px] font-normal text-white" />
              </label>
              {isImage && (
                <label className="flex items-start gap-2"><input type="checkbox" checked={own.is_cover} onChange={(e) => void patch({ is_cover: e.target.checked })} className="mt-0.5 h-4 w-4 accent-white" />
                  <span>Cover image<span className="block text-xs text-[#AEB8CC]">Shown on the card and the list row.</span></span></label>
              )}
              <label className="flex items-start gap-2"><input type="checkbox" checked={own.in_release_notes} onChange={(e) => void patch({ in_release_notes: e.target.checked })} className="mt-0.5 h-4 w-4 accent-white" />
                <span>Use in release notes<span className="block text-xs text-[#AEB8CC]">Marks it for the “What’s new” post. Blur client details first.</span></span></label>
              {previous && (
                <button type="button" onClick={() => setShowOriginal((v) => !v)} className="w-fit border-0 bg-transparent p-0 text-left text-xs font-semibold text-[#AFC4EA] underline">
                  {showOriginal ? 'Show the marked-up version' : 'Show the original'}
                </button>
              )}
            </>
          ) : (
            <div className="rounded-[10px] bg-[#3A2A12] px-3 py-2.5 text-xs text-[#F2D19A]">
              This came from a firm on {firm!.request_reference} and may show client details. Use <b>Mark up a copy</b> and <b>Blur</b> names, SSNs and amounts before sharing it anywhere.
            </div>
          )}
        </aside>
      </div>
      {files.length > 1 && !marking && (
        <footer className="flex items-center justify-center gap-2.5 overflow-x-auto border-t border-[#263040] p-3">
          {files.map((x, i) => <Thumb key={(x.own ?? x.firm)!.id} item={item} file={x} on={i === index} onClick={() => onIndex(i)} />)}
        </footer>
      )}
    </div>
  );
}

function Thumb({ item, file, on, onClick }: { item: ItemDetail; file: ViewerFile; on: boolean; onClick: () => void }) {
  const a = (file.own ?? file.firm)!;
  const img = a.mime_type.startsWith('image/') && a.scan === 'clean';
  const url = useAdminImage(img ? (file.firm ? requestFile(file.firm.request_id, a.id) : itemFile(item.id, a.id)) : null);
  return (
    <button type="button" onClick={onClick} aria-label={a.filename} aria-current={on || undefined}
      className={cx('flex h-14 w-[84px] flex-none items-center justify-center overflow-hidden rounded-md border-0 bg-white p-0', on ? 'ring-2 ring-white' : 'opacity-60 hover:opacity-100')}>
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <span className="text-[11px] font-bold text-bad">{a.filename.split('.').pop()?.toUpperCase()}</span>}
    </button>
  );
}
